import { useMemo, useState } from 'react'
import { SkeletonList } from '../componentes/Skeleton'
import { EstadoError } from '../componentes/EstadoError'
import { EstadoVacio } from '../componentes/EstadoVacio'
import { EnlaceWhatsApp } from '../componentes/EnlaceWhatsApp'
import { IconoActualizar } from '../componentes/iconos'
import { CargaRutas } from './agendamiento/CargaRutas'
import {
  useGestionSemana,
  useMarcarPendiente,
  type ClienteGestion,
  type CategoriaGestion,
  type ResultadoGestion,
} from '../hooks/usePendientes'

/**
 * Sección "Confirmar agendamiento" (B.9) — gestión telefónica de rezagados.
 *
 * El flujo normal lo decide el CLIENTE por WhatsApp. Esta pantalla es la
 * excepción: los que NO respondieron por WhatsApp antes del corte del jueves.
 * El equipo los llama jueves/viernes y REGISTRA el resultado de la llamada
 * (agendó / aplazó / no responde). Las tarjetas-KPI de arriba filtran la lista
 * por categoría; al marcar, el cliente cambia de categoría (no desaparece).
 */

/** Definición visual de cada categoría (tarjeta-KPI + chip). */
const CATEGORIAS: {
  clave: CategoriaGestion
  etiqueta: string
  tarjeta: string
  punto: string
  barra: string
}[] = [
  {
    clave: 'pendiente',
    etiqueta: 'Pendientes',
    tarjeta: 'data-[on=true]:ring-acento data-[on=true]:border-acento',
    punto: 'bg-acento',
    barra: 'bg-acento',
  },
  {
    clave: 'agendo',
    etiqueta: 'Agendó',
    tarjeta: 'data-[on=true]:ring-positivo-anillo data-[on=true]:border-positivo-anillo',
    punto: 'bg-positivo-anillo',
    barra: 'bg-positivo-anillo',
  },
  {
    clave: 'aplazo',
    etiqueta: 'Aplazó',
    tarjeta: 'data-[on=true]:ring-atencion-anillo data-[on=true]:border-atencion-anillo',
    punto: 'bg-atencion-anillo',
    barra: 'bg-atencion-anillo',
  },
  {
    clave: 'no_responde',
    etiqueta: 'No responde',
    tarjeta: 'data-[on=true]:ring-critico-anillo data-[on=true]:border-critico-anillo',
    punto: 'bg-critico-anillo',
    barra: 'bg-critico-anillo',
  },
]

/** Tarjeta-KPI pulsable: muestra el contador y filtra la lista al tocarla. */
function TarjetaKpi({
  etiqueta,
  valor,
  total,
  activa,
  punto,
  barra,
  tarjeta,
  onPulsar,
}: {
  etiqueta: string
  valor: number
  total: number
  activa: boolean
  punto: string
  barra: string
  tarjeta: string
  onPulsar: () => void
}) {
  const pct = total > 0 ? Math.round((valor / total) * 100) : 0
  return (
    <button
      type="button"
      onClick={onPulsar}
      data-on={activa}
      aria-pressed={activa}
      className={[
        'relative flex flex-col overflow-hidden rounded-xl border border-borde bg-superficie px-4 py-3 text-left transition',
        'hover:bg-elevado data-[on=true]:ring-1',
        tarjeta,
      ].join(' ')}
    >
      <span className="flex items-center gap-2 text-xs font-medium text-tinta-2">
        <span className={['size-2 rounded-full', punto].join(' ')} />
        {etiqueta}
      </span>
      <span className="mt-1.5 text-2xl font-bold tracking-tight text-tinta tabular-nums">{valor}</span>
      <span
        className={['absolute inset-x-0 bottom-0 h-0.5', barra].join(' ')}
        style={{ width: `${pct}%` }}
      />
    </button>
  )
}

/** Botón de acción de la gestión (agendó / aplazó / no responde). */
function BotonGestion({
  onPulsar,
  ocupado,
  tono,
  children,
}: {
  onPulsar: () => void
  ocupado: boolean
  tono: 'ok' | 'aviso' | 'neutro'
  children: React.ReactNode
}) {
  const clase =
    tono === 'ok'
      ? 'border-positivo-anillo bg-positivo-fondo text-positivo-tinta hover:brightness-110'
      : tono === 'aviso'
        ? 'border-atencion-anillo bg-atencion-fondo text-atencion-tinta hover:brightness-110'
        : 'border-borde-fuerte text-tinta-2 hover:bg-elevado'
  return (
    <button
      type="button"
      disabled={ocupado}
      onClick={onPulsar}
      className={[
        'rounded-lg border px-3 py-1.5 text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-50',
        clase,
      ].join(' ')}
    >
      {children}
    </button>
  )
}

/** Chip que muestra la categoría actual del cliente. */
function ChipCategoria({ categoria }: { categoria: CategoriaGestion }) {
  const map: Record<CategoriaGestion, { txt: string; cls: string }> = {
    pendiente: { txt: 'Pendiente', cls: 'text-acento-tinta bg-acento-suave border-acento-borde' },
    agendo: { txt: 'Agendó', cls: 'text-positivo-tinta bg-positivo-fondo border-positivo-anillo' },
    aplazo: { txt: 'Aplazó', cls: 'text-atencion-tinta bg-atencion-fondo border-atencion-anillo' },
    no_responde: { txt: 'No responde', cls: 'text-critico-tinta bg-critico-fondo border-critico-anillo' },
  }
  const { txt, cls } = map[categoria]
  return <span className={['shrink-0 rounded-full border px-2.5 py-0.5 text-xs font-semibold', cls].join(' ')}>{txt}</span>
}

function Fila({
  cliente,
  onMarcar,
  ocupado,
}: {
  cliente: ClienteGestion
  onMarcar: (estado: ResultadoGestion) => void
  ocupado: boolean
}) {
  return (
    <li className="flex flex-col gap-3 rounded-xl border border-borde bg-superficie px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex min-w-0 items-center gap-3">
        <ChipCategoria categoria={cliente.categoria} />
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="truncate text-sm font-medium text-tinta">{cliente.nombre}</span>
          <span className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-tinta-apagada">
            {cliente.telefono ? (
              <EnlaceWhatsApp
                telefono={cliente.telefono}
                etiqueta={cliente.telefono}
                className="text-acento-tinta underline underline-offset-2 hover:text-acento-fuerte"
              />
            ) : (
              <span>Sin teléfono</span>
            )}
            <span>{cliente.semanasSinPedir} sem. sin pedir</span>
            {cliente.aplazamientos > 0 ? (
              <span className="text-atencion-tinta">{cliente.aplazamientos} aplazamientos</span>
            ) : null}
          </span>
        </div>
      </div>

      <div className="flex shrink-0 gap-2">
        <BotonGestion tono="ok" ocupado={ocupado} onPulsar={() => onMarcar('agendado')}>
          Agendó
        </BotonGestion>
        <BotonGestion tono="aviso" ocupado={ocupado} onPulsar={() => onMarcar('aplazado')}>
          Aplazó
        </BotonGestion>
        <BotonGestion tono="neutro" ocupado={ocupado} onPulsar={() => onMarcar('cancelado')}>
          No responde
        </BotonGestion>
      </div>
    </li>
  )
}

export function ConfirmarAgendamiento() {
  const gestion = useGestionSemana()
  const marcar = useMarcarPendiente()
  const [filtro, setFiltro] = useState<CategoriaGestion | null>('pendiente')

  const datos = gestion.data
  const clientes = datos?.clientes ?? []
  const contadores = datos?.contadores ?? { pendiente: 0, agendo: 0, aplazo: 0, no_responde: 0, total: 0 }

  const visibles = useMemo(
    () => (filtro ? clientes.filter((c) => c.categoria === filtro) : clientes),
    [clientes, filtro],
  )

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-tinta">Confirmar agendamiento</h1>
          <p className="mt-1 text-sm text-tinta-apagada">
            Gestión telefónica de quien <strong className="text-tinta-2">no respondió por WhatsApp</strong>.
            Llámalos, y registra el resultado de la llamada. Toca una tarjeta para filtrar.
          </p>
        </div>
        <button
          type="button"
          aria-label="Actualizar la lista"
          onClick={() => void gestion.refetch()}
          className="flex items-center gap-2 rounded-lg border border-borde-fuerte bg-superficie px-3 py-2 text-sm font-medium text-tinta-2 transition-colors hover:bg-elevado"
        >
          <IconoActualizar className="size-4" />
          <span className="hidden sm:inline">Actualizar</span>
        </button>
      </div>

      {/* Tarjetas-KPI: contador por categoría + filtran al tocar (opción B). */}
      {!gestion.isPending && !gestion.isError ? (
        <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
          {CATEGORIAS.map((cat) => (
            <TarjetaKpi
              key={cat.clave}
              etiqueta={cat.etiqueta}
              valor={contadores[cat.clave]}
              total={contadores.total}
              activa={filtro === cat.clave}
              punto={cat.punto}
              barra={cat.barra}
              tarjeta={cat.tarjeta}
              onPulsar={() => setFiltro(filtro === cat.clave ? null : cat.clave)}
            />
          ))}
        </div>
      ) : null}

      {filtro ? (
        <button
          type="button"
          onClick={() => setFiltro(null)}
          className="w-fit text-xs font-medium text-acento-tinta underline underline-offset-2 hover:text-acento-fuerte"
        >
          Ver todos ({contadores.total})
        </button>
      ) : null}

      {/* Carga por ruta (B.7): contexto para saber qué día tiene hueco. */}
      <CargaRutas />

      {gestion.isPending ? (
        <SkeletonList />
      ) : gestion.isError ? (
        <EstadoError error={gestion.error} onReintentar={() => void gestion.refetch()} />
      ) : visibles.length === 0 ? (
        <EstadoVacio
          titulo={filtro ? 'Nadie en esta categoría' : 'No hay clientes esta semana'}
          descripcion={
            filtro
              ? 'Prueba con otra tarjeta, o pulsa "Ver todos".'
              : 'Aún no se ha lanzado la consulta de la semana, o no hay cartera activa.'
          }
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {visibles.map((cliente) => (
            <Fila
              key={cliente.contactId}
              cliente={cliente}
              ocupado={marcar.isPending && marcar.variables?.contactId === cliente.contactId}
              onMarcar={(estado) => marcar.mutate({ contactId: cliente.contactId, estado })}
            />
          ))}
        </ul>
      )}

      {marcar.isError ? (
        <p role="alert" className="text-sm text-critico-tinta">
          No se ha podido guardar la gestión. Inténtalo de nuevo.
        </p>
      ) : null}
    </div>
  )
}
